import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteError,
  useRouteLoaderData,
  type ShouldRevalidateFunction,
} from 'react-router'
import * as Sentry from '@sentry/react-router'
import {
  BodyScripts,
  HeadScripts,
  KeysScript,
  RootBootstrapScript,
} from './scripts'
import 'App.scss'
import { UserContext, type UserContextValue } from 'services/UserProvider'
import * as importedMeta from './root/meta'
import { translateManager } from 'services/TranslateManager'
import { KeysContext } from 'services/KeysProvider'
import { LangContext } from 'services/LangProvider'
import {
  ExperimentContext,
  type ExperimentContextValue,
} from 'services/ExperimentManager/ExperimentContext'
import { UserDeviceContext } from 'services/UserDeviceContext'
import DefaultProviders from 'DefaultProviders'
import { useInitExperimentClient } from './root/rootHelpers'
import { useEffect, useMemo, useState } from 'react'
import { useReloadOnNewServiceWorker } from '../hooks/useReloadOnNewServiceWorker'
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary'
import { SkewRecovery, RootErrorFallback } from './RootErrorRecovery'
import { AppErrorFallbackWrapper } from 'components/AppErrorFallback/AppErrorFallback'
import UserManager from '../services/UserManager'
import { UserSettingsProvider } from 'services/UserSettingsProvider'
import PromoCodeDialogContainer from 'components/PromoCodeDialogContainer/PromoCodeDialogContainer'
import { DarkModeWarningPopUp } from 'components/DarkModeDetector/DarkModeWarningPopUp'
import { rootLoader } from './root/loader.server'
import type { MainLayoutData } from './root/types'
import RootClientInit from './root/RootClientInit'

export const meta = importedMeta.meta
export const loader = rootLoader

export const shouldRevalidate: ShouldRevalidateFunction = () => false

export function Layout({ children }: { children: React.ReactNode }) {
  const data = useRouteLoaderData<typeof loader>('root')

  useEffect(() => {
    const rootElement = document.getElementById('root')
    if (rootElement) {
      rootElement.setAttribute('data-hydrated', 'true')
      window.hydratedByRemix = true
    } else {
      throw new Error('Root element not found')
    }
  }, [])

  // No root data on the client = version skew (stale tab vs newer deploy):
  // reload into the fresh build instead of crashing without providers.
  const isSkewFallback = !data || 'error' in data

  const content = isSkewFallback ? (
    <>
      {/* KeysScript so global singletons don't read an empty object and crash the fallback. */}
      <KeysScript keys={{}} />
      <ReactErrorBoundary fallbackRender={() => <SkewRecovery />}>
        {children}
      </ReactErrorBoundary>
      <Scripts />
    </>
  ) : (
    <AppErrorFallbackWrapper>
      <MainLayout data={data}>{children}</MainLayout>
    </AppErrorFallbackWrapper>
  )

  return (
    <html lang={isSkewFallback ? 'en' : data.language}>
      {/* The Usercentrics CMP scripts are injected as static markup at the top of
          this head in entry.server.tsx — they must precede every other script
          (React 19 hoists async scripts here) to block non-consented requests. */}
      <head>
        <Meta />
        <Links />
      </head>
      <body>
        <div id="root">{content}</div>
      </body>
    </html>
  )
}

function MainLayout({
  children,
  data,
}: {
  children: React.ReactNode
  data: MainLayoutData
}) {
  const {
    keys,
    user: initialUser,
    language,
    headerLanguage,
    features,
    experimentsUserId,
    initialPricingData,
    userSettings,
    utmParams,
    userDeviceData,
    forcedVariants,
    isMaterialsUnderFrameEnabled,
  } = data

  const [user, setUser] = useState(initialUser)

  const experimentClient = useInitExperimentClient({
    features,
    keys,
    experimentsUserId,
    language,
    utmParams,
    userDeviceData,
    forcedVariants,
    deviceId: initialUser.deviceId,
  })

  const experimentContextValue: ExperimentContextValue = useMemo(
    () => ({
      client: experimentClient,
      forcedVariants,
    }),
    [experimentClient, forcedVariants]
  )

  if (!translateManager.didInit) {
    translateManager.init(language)
  }

  useEffect(() => {
    UserManager.subscribeToUserChange((user: UserContextValue) => setUser(user))
  }, [])

  return (
    <>
      <UserSettingsProvider initialSettings={userSettings}>
        <UserDeviceContext.Provider value={userDeviceData}>
          <ExperimentContext.Provider value={experimentContextValue}>
            <LangContext.Provider value={language}>
              <KeysContext.Provider value={keys}>
                <UserContext.Provider value={user}>
                  <DefaultProviders initialPricingData={initialPricingData}>
                    <PromoCodeDialogContainer />
                    <DarkModeWarningPopUp />
                    <RootClientInit />
                    {children}
                  </DefaultProviders>
                </UserContext.Provider>
              </KeysContext.Provider>
            </LangContext.Provider>
          </ExperimentContext.Provider>
        </UserDeviceContext.Provider>
      </UserSettingsProvider>

      <KeysScript keys={keys} />
      <RootBootstrapScript
        data={{
          user: initialUser,
          language,
          headerLanguage,
          userSettings,
          isMaterialsUnderFrameEnabled,
        }}
      />

      <ScrollRestoration />
      <Scripts />

      <HeadScripts keys={keys} />
      <BodyScripts keys={keys} />
    </>
  )
}

export default function App() {
  useReloadOnNewServiceWorker()
  return <Outlet />
}

export function ErrorBoundary() {
  const error = useRouteError()
  // eslint-disable-next-line no-console
  console.error(error)

  if (!isRouteErrorResponse(error)) {
    Sentry.captureException(error)
  }

  return <RootErrorFallback />
}
